import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../../components/Layout';
import API_BASE_URL from '../../config/api';
import { Search, CheckCircle, XCircle, CreditCard, Building, Info, Calendar, RefreshCw, Wallet, Shield, Eye } from 'lucide-react';
import { useAlert } from '../../context/AlertContext';
import AutoRefreshIndicator from '../../components/AutoRefreshIndicator';

// Auto-refresh interval (30 seconds)
const AUTO_REFRESH_INTERVAL = 30000;

const WithdrawalRequests = ({ isEmbedded = false }) => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'pending', 'approved', 'rejected'
    const [showModerationModal, setShowModerationModal] = useState(false);
    const [modAction, setModAction] = useState({ id: null, type: '', user: '' });
    const [modRemarks, setModRemarks] = useState('');
    const [processingId, setProcessingId] = useState(null);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { showAlert } = useAlert();

    // Auto-refresh state
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const intervalRef = useRef(null);
    const isMountedRef = useRef(true);

    const fetchRequests = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        else setIsRefreshing(true);

        try {
            const token = localStorage.getItem('authToken');
            const url = new URL(`${API_BASE_URL}/admin/get_withdrawal_requests.php`);
            url.searchParams.append('status', statusFilter);
            if (fromDate) url.searchParams.append('from', fromDate);
            if (toDate) url.searchParams.append('to', toDate);
            url.searchParams.append('_t', Date.now());

            const response = await fetch(url.toString(), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success && isMountedRef.current) {
                setRequests(data.requests);
                setLastUpdated(new Date());
            }
        } catch (error) {
            console.error("Failed to fetch requests", error);
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
                setIsRefreshing(false);
            }
        }
    }, [statusFilter, fromDate, toDate]);

    // Initial fetch and status filter change
    useEffect(() => {
        isMountedRef.current = true;
        fetchRequests(false);

        return () => {
            isMountedRef.current = false;
        };
    }, [fetchRequests]);

    // Auto-refresh interval
    useEffect(() => {
        if (autoRefreshEnabled) {
            intervalRef.current = setInterval(() => {
                fetchRequests(true); // Silent refresh
            }, AUTO_REFRESH_INTERVAL);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [autoRefreshEnabled, fetchRequests]);

    const toggleAutoRefresh = () => setAutoRefreshEnabled(prev => !prev);

    const openModeration = (req, type) => {
        setModAction({ id: req.id, type: type, user: req.user_name });
        setModRemarks(type === 'approve' ? 'Transaction processed successfully' : 'Request rejected due to invalid details');
        setShowModerationModal(true);
    };

    const handleAction = async () => {
        const { id, type } = modAction;
        setProcessingId(id);
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/admin/process_withdrawal.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    request_id: id,
                    action: type,
                    remarks: modRemarks
                })
            });
            const data = await response.json();
            if (data.success) {
                fetchRequests();
                showAlert(data.message, 'success');
                setShowModerationModal(false);
            } else {
                showAlert(data.message, 'error');
            }
        } catch (error) {
            console.error("Failed to process request", error);
            showAlert('Failed to process request', 'error');
        } finally {
            setProcessingId(null);
        }
    };

    const filteredRequests = requests.filter(r =>
        (r.user_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.user_code || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const Container = isEmbedded ? React.Fragment : Layout;
    const containerProps = isEmbedded ? {} : { title: "Withdrawal Requests" };

    return (
        <Container {...containerProps}>
            <div className="space-y-6">
                {!isEmbedded && (
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 bg-gradient-to-br from-white to-gray-50/50 p-8 rounded-[2.5rem] shadow-sm border border-gray-100 mt-2">
                        <div className="flex items-center gap-5">
                            <div className="p-4 bg-blue-600 rounded-3xl shadow-xl shadow-blue-100">
                                <Wallet className="text-white" size={32} />
                            </div>
                            <div>
                                <h1 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight leading-tight">Withdrawal Requests</h1>
                                <p className="text-gray-500 font-bold mt-1 uppercase text-[10px] tracking-[0.2em] flex items-center gap-2">
                                    <Shield size={12} className="text-blue-500" />
                                    Financial Operations Dashboard
                                </p>
                            </div>
                        </div>
                        <AutoRefreshIndicator
                            lastUpdated={lastUpdated}
                            isAutoRefreshEnabled={autoRefreshEnabled}
                            onToggle={toggleAutoRefresh}
                            onRefresh={() => fetchRequests(false)}
                            loading={isRefreshing}
                            intervalSeconds={30}
                        />
                    </div>
                )}

                <div className="bg-white rounded-[2rem] p-4 shadow-sm border border-gray-100 flex flex-col xl:flex-row justify-between items-center gap-4">
                    <div className="relative flex-1 w-full">
                        <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="text"
                            placeholder="Search by User Code, Name or Email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-14 pr-4 py-4 bg-gray-50 border-none rounded-3xl focus:ring-2 focus:ring-blue-500 focus:outline-none font-bold text-sm placeholder:text-gray-400 placeholder:font-medium transition-all"
                        />
                    </div>

                    <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto">
                        <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-2xl border border-gray-100 cursor-pointer hover:border-blue-200 transition-colors" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                            <Calendar size={18} className="text-gray-400" />
                            <div className="flex flex-col">
                                <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest leading-none">From Date</span>
                                <input
                                    type="date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    className="bg-transparent text-xs font-black focus:outline-none cursor-pointer text-gray-700"
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-2xl border border-gray-100 cursor-pointer hover:border-blue-200 transition-colors" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                            <Calendar size={18} className="text-gray-400" />
                            <div className="flex flex-col">
                                <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest leading-none">To Date</span>
                                <input
                                    type="date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    className="bg-transparent text-xs font-black focus:outline-none cursor-pointer text-gray-700"
                                />
                            </div>
                        </div>

                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="px-6 py-4 border-none border-gray-100 rounded-3xl focus:ring-2 focus:ring-blue-500 focus:outline-none bg-gray-50 text-gray-700 font-bold text-sm shadow-inner transition-all appearance-none cursor-pointer min-w-[180px]"
                        >
                            <option value="all">Total Requests</option>
                            <option value="pending">Pending Only</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                        <button
                            onClick={() => fetchRequests()}
                            className="px-8 py-4 bg-gray-900 text-white text-xs font-black uppercase tracking-[0.2em] rounded-3xl hover:bg-black transition-all shadow-xl shadow-gray-200 flex items-center justify-center gap-3 active:scale-95 w-full sm:w-auto"
                        >
                            <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
                            Apply Filters
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 overflow-hidden">
                    {/* Desktop Table View */}
                    <div className="hidden lg:block overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50/50 border-b border-gray-100 text-gray-400 text-[10px] font-black uppercase tracking-[0.2em]">
                                <tr>
                                    <th className="px-8 py-6 text-left">Growth Partner</th>
                                    <th className="px-8 py-6 text-left">Capital Payout</th>
                                    <th className="px-8 py-6 text-left">Destination</th>
                                    <th className="px-8 py-6 text-left">Timeline</th>
                                    <th className="px-8 py-6 text-center">Status</th>
                                    <th className="px-8 py-6 text-right">Moderation</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {loading ? (
                                    <tr><td colSpan="6" className="p-20 text-center"><div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div></td></tr>
                                ) : filteredRequests.length === 0 ? (
                                    <tr><td colSpan="6" className="p-20 text-center text-gray-400 font-bold uppercase tracking-widest text-sm">No Payout Requests Found</td></tr>
                                ) : (
                                    filteredRequests.map((req) => (
                                        <tr key={req.id} className="hover:bg-gray-50/50 transition-colors group border-b border-gray-50 last:border-none">
                                            <td className="px-8 py-6">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center text-gray-400 font-black group-hover:bg-blue-600 group-hover:text-white transition-all shadow-sm">
                                                        {(req.user_name || 'U').charAt(0)}
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="font-black text-gray-900 group-hover:text-blue-600 transition-colors uppercase text-sm tracking-tight">{req.user_name}</span>
                                                        <span className="text-[10px] font-bold text-gray-400 font-mono tracking-tighter mt-0.5">{req.user_code} • {req.email}</span>
                                                        <span className={`text-[9px] font-black mt-2 w-fit px-1.5 py-0.5 rounded border ${req.source_wallet === 'income_wallet' ? 'bg-indigo-50 text-indigo-600 border-indigo-100' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                                                            {req.source_wallet === 'income_wallet' ? 'INCOME WALLET' : 'MAIN WALLET'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6">
                                                <div className="flex flex-col">
                                                    <span className="font-black text-gray-900 text-lg tracking-tighter">₹ {parseFloat(req.amount).toLocaleString('en-IN')}</span>
                                                    <div className="flex items-center gap-1.5 mt-1">
                                                        <span className="text-[10px] font-bold text-green-600">Net: ₹ {parseFloat(req.net_amount).toLocaleString('en-IN')}</span>
                                                        <span className="text-[10px] font-bold text-red-400 line-through opacity-50">₹ {parseFloat(req.amount).toLocaleString('en-IN')}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6">
                                                {req.payout_method === 'upi' ? (
                                                    <div className="flex flex-col gap-1.5">
                                                        <div className="flex items-center gap-2 text-blue-600">
                                                            <CreditCard size={14} />
                                                            <span className="text-[10px] font-black uppercase tracking-widest">UPI ID</span>
                                                        </div>
                                                        <span className="text-sm font-black text-gray-900 bg-blue-50/50 px-2 py-1 rounded-lg border border-blue-100 w-fit">{req.withdrawal_upi || req.profile_upi || 'MISSING'}</span>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col gap-1">
                                                        <div className="flex items-center gap-2 text-indigo-600">
                                                            <Building size={14} />
                                                            <span className="text-[10px] font-black uppercase tracking-widest">{req.bank_name || 'BANK TRANSFER'}</span>
                                                        </div>
                                                        <span className="text-sm font-black text-gray-900 tracking-tighter leading-none">{req.account_number}</span>
                                                        <div className="flex gap-2 mt-1 items-center">
                                                            <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-1.5 rounded uppercase tracking-tighter">{req.ifsc_code}</span>
                                                            <span className="text-[10px] font-bold text-gray-400 truncate max-w-[100px] uppercase tracking-tighter">{req.account_holder}</span>
                                                            {req.account_type && <span className="text-[8px] font-black text-indigo-500 bg-indigo-50 px-1 rounded uppercase tracking-tighter">{req.account_type}</span>}
                                                            {req.passbook_image && (
                                                                <a href={req.passbook_image} target="_blank" rel="noreferrer" className="ml-1 p-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors" title="View Passbook">
                                                                    <Eye size={10} />
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-8 py-6">
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-sm font-black text-gray-900 tracking-tighter">{new Date(req.request_date).toLocaleDateString()}</span>
                                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">{new Date(req.request_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6 text-center">
                                                <span className={`px-5 py-2 rounded-xl text-[10px] font-black tracking-widest shadow-sm border ${req.status === 'approved' ? 'bg-green-100 text-green-700 border-green-200' :
                                                    req.status === 'rejected' ? 'bg-red-100 text-red-700 border-red-200' :
                                                        'bg-amber-100 text-amber-700 border-amber-200 animate-pulse'
                                                    }`}>
                                                    {req.status.toUpperCase()}
                                                </span>
                                            </td>
                                            <td className="px-8 py-6 text-right">
                                                {req.status === 'pending' ? (
                                                    <div className="flex justify-end gap-3 scale-in">
                                                        <button
                                                            onClick={() => openModeration(req, 'approve')}
                                                            disabled={processingId === req.id}
                                                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all font-black text-[10px] tracking-widest uppercase shadow-lg shadow-green-100 disabled:opacity-50"
                                                        >
                                                            <CheckCircle size={14} />
                                                            Approve
                                                        </button>
                                                        <button
                                                            onClick={() => openModeration(req, 'reject')}
                                                            disabled={processingId === req.id}
                                                            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all font-black text-[10px] tracking-widest uppercase shadow-lg shadow-red-100 disabled:opacity-50"
                                                        >
                                                            <XCircle size={14} />
                                                            Reject
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="text-[10px] font-black text-gray-300 uppercase tracking-widest flex items-center justify-end gap-1.5">
                                                        <Info size={12} />
                                                        Archived
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="lg:hidden space-y-4 px-4">
                        {loading ? (
                            <div className="p-12 text-center">
                                <div className="w-10 h-10 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                                <p className="text-gray-400 font-bold uppercase text-[10px] tracking-widest">Loading Payouts...</p>
                            </div>
                        ) : filteredRequests.length === 0 ? (
                            <div className="p-20 text-center bg-white rounded-[2.5rem] border border-gray-100">
                                <Wallet className="mx-auto text-gray-200 mb-4" size={48} />
                                <p className="text-gray-400 font-bold uppercase text-[10px] tracking-widest">No payout records found</p>
                            </div>
                        ) : (
                            filteredRequests.map((req) => (
                                <div key={req.id} className="bg-white rounded-[2.5rem] p-6 shadow-sm border border-gray-100 flex flex-col gap-6 animate-fadeIn hover:shadow-md transition-all">
                                    <div className="flex justify-between items-start">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center text-gray-400 font-black shadow-sm order border-white">
                                                {(req.user_name || 'U').charAt(0)}
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="font-black text-gray-900 uppercase text-sm tracking-tight">{req.user_name}</span>
                                                <span className="text-[10px] font-bold text-gray-400 font-mono tracking-tighter mt-0.5">{req.user_code}</span>
                                                <span className={`text-[8px] font-black mt-2 w-fit px-2 py-0.5 rounded-lg border shadow-sm ${req.source_wallet === 'income_wallet' ? 'bg-indigo-50 text-indigo-600 border-indigo-100' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                                                    {req.source_wallet === 'income_wallet' ? 'INCOME WALLET' : 'MAIN WALLET'}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-end">
                                            <span className="font-black text-gray-900 text-xl tracking-tighter">₹ {parseFloat(req.amount).toLocaleString('en-IN')}</span>
                                            <span className="text-[10px] font-black text-green-600 mt-1">NET: ₹ {parseFloat(req.net_amount).toLocaleString('en-IN')}</span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
                                            <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-3">Payout Destination</span>
                                            {req.payout_method === 'upi' ? (
                                                <div className="flex flex-col gap-2">
                                                    <div className="flex items-center gap-2 text-blue-600">
                                                        <CreditCard size={14} />
                                                        <span className="text-[9px] font-black uppercase tracking-widest">UPI ID</span>
                                                    </div>
                                                    <span className="text-xs font-black text-gray-900 break-all bg-white px-3 py-2 rounded-xl border border-blue-100">{req.withdrawal_upi || req.profile_upi || 'MISSING'}</span>
                                                </div>
                                            ) : (
                                                <div className="flex flex-col gap-1.5">
                                                    <div className="flex items-center gap-2 text-indigo-600 mb-1">
                                                        <Building size={14} />
                                                        <span className="text-[9px] font-black uppercase truncate tracking-widest">{req.bank_name || 'BANK'}</span>
                                                    </div>
                                                    <span className="text-xs font-black text-gray-900 font-mono tracking-tighter">{req.account_number}</span>
                                                    <div className="flex gap-2 items-center">
                                                        <span className="text-[9px] font-bold text-gray-400 bg-white px-1.5 py-0.5 rounded border border-gray-100">{req.ifsc_code}</span>
                                                        <span className="text-[9px] font-bold text-gray-400 truncate tracking-tight">{req.account_holder}</span>
                                                        {req.passbook_image && (
                                                            <a href={req.passbook_image} target="_blank" rel="noreferrer" className="p-1 bg-white text-blue-600 rounded border border-blue-100" title="View Passbook">
                                                                <Eye size={10} />
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100 flex flex-col justify-between">
                                            <div>
                                                <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-3">Request Timeline</span>
                                                <div className="flex flex-col">
                                                    <span className="text-xs font-black text-gray-900">{new Date(req.request_date).toLocaleDateString()}</span>
                                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{new Date(req.request_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                </div>
                                            </div>
                                            <span className={`mt-4 w-fit px-4 py-1.5 rounded-xl text-[9px] font-black tracking-widest border shadow-sm ${req.status === 'approved' ? 'bg-green-100 text-green-700 border-green-200' :
                                                req.status === 'rejected' ? 'bg-red-100 text-red-700 border-red-200' :
                                                    'bg-amber-100 text-amber-700 border-amber-200 animate-pulse'
                                                }`}>
                                                {req.status.toUpperCase()}
                                            </span>
                                        </div>
                                    </div>

                                    {req.status === 'pending' ? (
                                        <div className="grid grid-cols-2 gap-4">
                                            <button
                                                onClick={() => openModeration(req, 'approve')}
                                                disabled={processingId === req.id}
                                                className="flex items-center justify-center gap-3 py-4 bg-gray-900 text-white rounded-3xl hover:bg-black transition-all font-black text-[10px] tracking-widest uppercase shadow-xl shadow-gray-200 disabled:opacity-50 active:scale-95"
                                            >
                                                <CheckCircle size={16} />
                                                Approve
                                            </button>
                                            <button
                                                onClick={() => openModeration(req, 'reject')}
                                                disabled={processingId === req.id}
                                                className="flex items-center justify-center gap-3 py-4 bg-white text-red-600 border-2 border-red-50 rounded-3xl hover:bg-red-50 transition-all font-black text-[10px] tracking-widest uppercase shadow-sm disabled:opacity-50 active:scale-95"
                                            >
                                                <XCircle size={16} />
                                                Reject
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="py-4 bg-gray-50 rounded-3xl border border-dashed border-gray-200 text-center">
                                            <span className="text-[9px] font-black text-gray-300 uppercase tracking-[0.2em] flex items-center justify-center gap-2">
                                                <Info size={14} />
                                                Archived Entry
                                            </span>
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Moderation Modal */}
            {showModerationModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden animate-scale-in border border-gray-100">
                        <div className={`p-6 text-white ${modAction.type === 'approve' ? 'bg-green-600' : 'bg-red-600'}`}>
                            <h2 className="text-xl font-black uppercase tracking-widest flex items-center gap-2">
                                {modAction.type === 'approve' ? <CheckCircle size={24} /> : <XCircle size={24} />}
                                {modAction.type.toUpperCase()} REQUEST
                            </h2>
                            <p className="text-[10px] font-bold opacity-80 mt-1 uppercase tracking-widest">Processing for {modAction.user}</p>
                        </div>

                        <div className="p-8 space-y-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Admin Remarks / Reason</label>
                                <textarea
                                    value={modRemarks}
                                    onChange={(e) => setModRemarks(e.target.value)}
                                    placeholder="Enter reason or transaction details..."
                                    className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl p-4 text-sm font-bold text-gray-700 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50/50 transition-all min-h-[120px]"
                                />
                            </div>

                            <div className="flex gap-4">
                                <button
                                    onClick={() => setShowModerationModal(false)}
                                    className="flex-1 py-4 text-xs font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-xl transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAction}
                                    disabled={processingId === modAction.id}
                                    className={`flex-1 py-4 text-xs font-black uppercase tracking-widest text-white rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 ${modAction.type === 'approve'
                                        ? 'bg-green-600 hover:bg-green-700 shadow-green-100'
                                        : 'bg-red-600 hover:bg-red-700 shadow-red-100'
                                        }`}
                                >
                                    {processingId === modAction.id ? (
                                        <div className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full"></div>
                                    ) : (
                                        <>CONFIRM {modAction.type.toUpperCase()}</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .animate-fade-in { animation: fadeIn 0.3s ease-out; }
                .animate-scale-in { animation: scaleIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
            `}</style>
        </Container>
    );
};

export default WithdrawalRequests;
